
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { LoginAction } from "@components/login/helper";
import { buildClass, checkClass } from "@utils/tag";
import { useState, useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import PhoneInputController from "@components/FormControllers/controllers/PhoneInputController";
import PasswordInputController from "@components/FormControllers/controllers/PasswordInputController";
import BtnBase from "@components/Buttons/BtnBase";
import InputController from "@components/FormControllers/controllers/InputController";
import { handleError } from "@network/handle-errors";
import { useDispatch, useSelector } from "noval";
import { useRouter } from "next/router";
import { http } from "@network/http";
import { generateUniqueId, Icon, Text } from "@utils/helper";
import useTranslation from "next-translate/useTranslation";
import RenderMeta from "@utils/RenderMeta";
import Yup, { globalValidations } from "@utils/yup";
import dynamic from "next/dynamic";
import CustomImage from "@components/CustomImage";
const OtpModal = dynamic(() => import("@components/modals/OtpModal"), {
    ssr: false
});
const Link = dynamic(() => import("next/link"), {
    ssr: true
});
const { requiredString, phoneValidator } = globalValidations;
const createSchema = (action) => {
    const switchInput = {
        1: {
            phone_country_iso2: requiredString,
            phone: phoneValidator
        },
        2: {
            c_number: requiredString
        }
    };
    return Yup.object().shape({
        password: requiredString.matches(/^\S*$/, "password.space"),
        ...switchInput[action]
    });
};
export default function Login() {
    const router = useRouter();
    const { push, query } = router;
    const { dispatch } = useDispatch();
    const action = useSelector("action", +query.action || 1);
    const renderSchema = createSchema(action);
    const { t } = useTranslation("login");
    const [screen, savedPath] = useSelector(["screen", "savedPath"]);
    const [loading, setLoading] = useState(false);
    const [otpModal, setOtpModal] = useState({ open: false });
    const isIndividual = action === 1;
    const { control, handleSubmit, setError } = useForm({
        resolver: yupResolver(renderSchema),
        defaultValues: {
            phone: "",
            password: "",
            phone_country_iso2: "SA"
        }
    });
    const handleLogin = useCallback(async (values) => {
        setLoading(true);
        const data = { ...values };
        try {
            if (!isIndividual) {
                delete data["phone"];
                delete data["phone_country_iso2"];
            }
            else {
                delete data["c_number"];
            }
            const response = await http.post("/login", data);
            const vid = response?.data?.data?.vid || "";
            const is_verified = response?.data?.data?.is_verified;
            setOtpModal({
                open: true,
                showNin: !is_verified,
                data: { ...data, vid },
                digitsLength: is_verified ? 4 : 6,
                onResend: () => handleLogin(values),
                callback: (res) => {
                    const jwt = res.data?.data?.token;
                    window.localStorage.setItem("jwt", jwt);
                    dispatch("authentication", {
                        callback: ({ kyc_suitability, is_iqama_updated, is_kyc_completed, unsuitability_confirmation, }) => {
                            savedPath ? push(savedPath) : push("/home");
                            dispatch({ savedPath: undefined, randId: generateUniqueId() });
                            setOtpModal({ open: false });
                            if (!is_iqama_updated) {
                                dispatch({ iqamaAlert: true });
                            }
                            if (!is_kyc_completed) {
                                dispatch({ reminderOpen: true });
                            }
                            else if (kyc_suitability || unsuitability_confirmation) {
                            }
                            else {
                                dispatch({ isOpen: true, suitable: false }, "suitability");
                            }
                        }
                    });
                },
                api_url: is_verified
                    ? "/login/verify-two-factor-auth"
                    : "/absher/verify"
            });
        }
        catch (error) {
            handleError(error, { setError });
        }
        finally {
            setLoading(false);
        }
    }, [isIndividual]);
    return (<>
      <RenderMeta title={`${t("common-locale:navbar.login")} | ${t("common-locale:aseel")}`} description={t("common-locale:global_description")} img="/images/ogi/home.jpg"/>
      <div tabIndex={0} style={{
            backgroundSize: "auto",
            backgroundImage: "url(/images/background/back-login.webp)"
        }} className="flex flex-col items-center justify-center w-full h-screen gap-8 md:gap-14 pt-4 pb-5">
        <Icon onClick={() => push("/")} className="cursor-pointer" width="139px" height="56px" id="LogoWhite" viewBox="0 0 173 64"/>

        <div className={buildClass("rounded-[20px] bg-[#F8FBFF] flex flex-col justify-start items-center overflow-hidden", checkClass(screen === "sm-phone", [
            "min-w-[315px] max-w-[400px] w-full",
            "w-[416px]",
        ]))}>
          <LoginAction isIndividual={isIndividual}/>
          <form onSubmit={handleSubmit(handleLogin)} className="flex flex-col justify-start items-center w-full gap-7 px-3 md:px-8 py-8">
            {isIndividual ? (<PhoneInputController control={control} inputSize="16px" name="phone" removeText inputBg="#FFFFFF" phoneVal={<>
                    <CustomImage width={24} height={24} className="w-6 h-auto" src="/images/global/ksa.png" alt="ksa"/>
                    <Text>966</Text>
                  </>} inputConstrains={{ maxLength: 9 }} clsName="w-full gap-[18px]" customLabel={t("labels:phone")} classNameLabel="leading-[21px] font-bold" placeholder={t("placeholders:phone")} sx={{ boxShadow: "0px 8px 16px -25px #00000005" }}/>) : (<InputController control={control} removeText inputBg="#FFFFFF" name="c_number" clsName="w-full gap-[18px]" classNameLabel="leading-[21px] font-bold" customLabel={t("labels:c_number")} placeholder={t("labels:establishment_unified_number")} sx={{ boxShadow: "0px 8px 16px -25px #00000005" }} inputSize="16px"/>)}
            <PasswordInputController name="password" clsName="w-full" inputBg="#FFFFFF" control={control} customLabel={t("labels:password")} placeholder="● ● ● ● ● ● ● ●" sx={{ boxShadow: "0px 8px 16px -25px #00000005" }} fontFamily="math, sans-serif" inputSize="16px"/>
            <div className="flex flex-col w-full items-center gap-[17px]">
              <BtnBase type="submit" variant="contained" sx={{ width: "100%", height: "45", borderRadius: "48px" }} isLoading={loading} className="items-center justify-center">
                <Text color="#fff" fontType="Expo_sim" weight={500} className="capitalize">
                  {t("signIn")}
                </Text>
              </BtnBase>
            </div>
            <Text size="14px" color="#0366FF" className="capitalize">
              <Link href={`/forgot?type=${isIndividual ? "nin" : "c_numbe"}`}>{t("forgot")}</Link>
            </Text>
          </form>
        </div>
        <div className="flex gap-2 items-center">
          <Text size={screen === "sm-phone" ? "13px" : "16px"} color="#DEEEFF66" fontType="Expo_med">
            {t("noAccount")}
          </Text>
          <Link href="/register">
            <Text size={screen === "sm-phone" ? "13px" : "16px"} color="#fff" fontType="Expo_sim">
              {t("signUp")}
            </Text>
          </Link>
        </div>
        {otpModal.open && (<OtpModal {...otpModal} onClose={() => setOtpModal({ open: false })}/>)}
      </div>
    </>);
}

    async function __Next_Translate__getStaticProps__19676fb44c4__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/login',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19676fb44c4__ as getStaticProps }
  